import moment from 'moment';
import { formatIncompletePhoneNumber } from 'libphonenumber-js';
import { GENDER_OPTIONS, YES_NO_OPTIONS, SUPERVISOR_OPTIONS, SKILLED_NON_SKILLED_OPTIONS } from 'utils/optionsUtil';
import { GOV_ID_TYPE_OTHER_OPTION_ID } from 'constants/common';

export const WORKER_PROFILE_FIELD_CODE = {
  Email: 'WorkerContactEmail',
  MobilePhone: 'WorkerContactMobileNumber',
  AlternateMobileNumber: 'WorkerContactAlternateMobileNumber',
  FirstName: 'WorkerFirstName',
  MiddleName: 'WorkerMiddleName',
  LastName: 'WorkerLastName',
  DateOfBirth: 'WorkerDateOfBirth',
  Gender: 'WorkerGender',
  Ethnicity: 'WorkerEthnicity',
  PrimaryLanguage: 'WorkerPrimaryLanguage',
  VeteranStatus: 'WorkerVeteranStatus',
  Allergies: 'WorkerAllergies',
  LastSSN: 'WorkerLastSSN',
  HomeAddressLine1: 'WorkerHomeAddressLine1',
  HomeAddressLine2: 'WorkerHomeAddressLine2',
  HomeAddressCity: 'WorkerHomeAddressCity',
  HomeAddressStateProvince: 'WorkerHomeAddressStateProvince',
  HomeAddressZipPostal: 'WorkerHomeAddressZipPostal',
  HomeAddressCountry: 'WorkerHomeAddressCountry',
  EmergencyContactName: 'WorkerEmergencyContactName',
  EmergencyContactMobilePhone: 'WorkerEmergencyContactMobilePhone',
  EmergencyContactRelationship: 'WorkerEmergencyContactRelationship',
  GovernmentIDType: 'WorkerGovernmentIDType',
  GovernmentIDNumber: 'WorkerGovernmentIDNumber',
  GovernmentIDIssuedBy: 'WorkerGovernmentIDIssuedBy',
  GovernmentIDImage: 'WorkerGovernmentIDImage',
  WorkExperience: 'WorkerWorkExperience',
  Role: 'WorkerRole',
  LaborUnionStatus: 'WorkerLaborUnionStatus',
  LaborUnionNumber: 'WorkerLaborUnionNumber',
  Trades: 'WorkerTrades',
  ProfilePhoto: 'WorkerProfilePhoto',
  EmployerName: 'WorkerEmployerName',
  EmailOrMobilePhone: 'WorkerContactEmailOrMobilePhone',
} as const;

export type WorkerProfileFieldCode = (typeof WORKER_PROFILE_FIELD_CODE)[keyof typeof WORKER_PROFILE_FIELD_CODE];

/** Maps a worker profile field code to its key in the onboarding state */
export const FIELD_CODE_TO_ONBOARDING_KEY: Record<string, string> = {
  WorkerContactEmail: 'email',
  WorkerContactMobileNumber: 'mobilePhoneNumber',
  WorkerContactAlternateMobileNumber: 'phoneNumber',
  WorkerFirstName: 'firstName',
  WorkerMiddleName: 'middleName',
  WorkerLastName: 'lastName',
  WorkerDateOfBirth: 'dateOfBirth',
  WorkerGender: 'gender',
  WorkerEthnicity: 'ethnicity',
  WorkerPrimaryLanguage: 'primaryLanguage',
  WorkerVeteranStatus: 'isVeteran',
  WorkerAllergies: 'allergies',
  WorkerLastSSN: 'socialSecurityNumber',
  WorkerHomeAddressLine1: 'line1',
  WorkerHomeAddressLine2: 'line2',
  WorkerHomeAddressCity: 'city',
  WorkerHomeAddressStateProvince: 'stateName',
  WorkerHomeAddressZipPostal: 'zipCode',
  WorkerHomeAddressCountry: 'country',
  WorkerEmergencyContactName: 'emergencyContactName',
  WorkerEmergencyContactMobilePhone: 'emergencyContactPhone',
  WorkerEmergencyContactRelationship: 'emergencyContactRelationship',
  WorkerGovernmentIDType: 'identificationType',
  WorkerGovernmentIDNumber: 'identificationNumber',
  WorkerGovernmentIDIssuedBy: 'identificationGeographicLocation',
  WorkerWorkExperience: 'isSkilled',
  WorkerRole: 'isSupervisor',
  WorkerLaborUnionStatus: 'isAffiliatedToLaborUnion',
  WorkerLaborUnionNumber: 'laborUnionNumber',
  WorkerTrades: 'trades',
  WorkerEmployerName: 'companies',
  WorkerProfilePhoto: 'pictureUrl',
  WorkerGovernmentIDImage: 'governmentDocumentFiles',
};

/**
 * When WorkerContactEmailOrMobilePhone is present in `fields`, removes it and
 * substitutes individual Email and/or MobilePhone entries for whichever has a value.
 * `definitionSource` is the unfiltered field list used to look up field metadata for
 * synthesized entries; it defaults to `fields` itself when not provided.
 */
export function expandEmailOrMobilePhoneField(
  fields: any[],
  emailValue: string | undefined,
  phoneValue: string | undefined,
  definitionSource: any[] = fields,
): any[] {
  const emailOrPhoneIdx = fields.findIndex(f => f.code === WORKER_PROFILE_FIELD_CODE.EmailOrMobilePhone);
  if (emailOrPhoneIdx === -1) return fields;

  const withoutComposite = fields.filter(f => f.code !== WORKER_PROFILE_FIELD_CODE.EmailOrMobilePhone);

  const hasEmailEntry = withoutComposite.some(f => f.code === WORKER_PROFILE_FIELD_CODE.Email);
  const hasPhoneEntry = withoutComposite.some(f => f.code === WORKER_PROFILE_FIELD_CODE.MobilePhone);

  const synthFields: any[] = [];
  if (!hasEmailEntry && emailValue) {
    const def = definitionSource.find(f => f.code === WORKER_PROFILE_FIELD_CODE.Email);
    synthFields.push({ ...(def || {}), code: WORKER_PROFILE_FIELD_CODE.Email, name: def?.name || 'Email', value: emailValue, dataType: def?.dataType ?? 1 });
  }
  if (!hasPhoneEntry && phoneValue) {
    const def = definitionSource.find(f => f.code === WORKER_PROFILE_FIELD_CODE.MobilePhone);
    synthFields.push({ ...(def || {}), code: WORKER_PROFILE_FIELD_CODE.MobilePhone, name: def?.name || 'Mobile Phone', value: phoneValue, dataType: def?.dataType ?? 3 });
  }

  return [
    ...withoutComposite.slice(0, emailOrPhoneIdx),
    ...synthFields,
    ...withoutComposite.slice(emailOrPhoneIdx),
  ];
}

const ADDRESS_KEYS = new Set(['line1', 'line2', 'city', 'stateName', 'zipCode', 'country']);

type OptionSet = { en: { id: any; name: string }[]; es: { id: any; name: string }[] };

const BOOLEAN_FIELD_OPTIONS: Record<string, OptionSet> = {
  WorkerGender: GENDER_OPTIONS,
  WorkerRole: SUPERVISOR_OPTIONS,
  WorkerWorkExperience: SKILLED_NON_SKILLED_OPTIONS,
};

const resolveImageUrls = (raw: any): string[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((item: any) => item?.url).filter(Boolean);
  if (typeof raw === 'string') return [raw];
  return raw?.url ? [raw.url] : [];
};

export const getStoredImageUrls = (code: string, onboarding: any): string[] => {
  const stateKey = FIELD_CODE_TO_ONBOARDING_KEY[code];
  if (!stateKey) return [];
  return resolveImageUrls(onboarding?.[stateKey]);
};

export const getStoredFieldDisplayValue = (
  code: string,
  onboarding: any,
  languageCode: string,
  dataType?: number,
): string => {
  if (dataType === 7) {
    const email = onboarding?.email;
    const phone = onboarding?.mobilePhoneNumber;
    return email || (phone ? formatIncompletePhoneNumber(phone) : '') || '';
  }

  // With ID type "Other", Issued By is free text stored in otherIdentificationType instead of a location
  if (
    code === WORKER_PROFILE_FIELD_CODE.GovernmentIDIssuedBy &&
    onboarding?.identificationType?.id === GOV_ID_TYPE_OTHER_OPTION_ID
  ) {
    return onboarding?.otherIdentificationType || '';
  }

  const stateKey = FIELD_CODE_TO_ONBOARDING_KEY[code];
  if (!stateKey) return '';

  const lang = (languageCode || 'en').toLowerCase() as 'en' | 'es';
  const rawValue = ADDRESS_KEYS.has(stateKey) ? onboarding?.address?.[stateKey] : onboarding?.[stateKey];

  if (rawValue === null || rawValue === undefined || rawValue === '') return '';

  if (dataType === 6) {
    return resolveImageUrls(rawValue)[0] ?? '';
  }

  if (dataType === 4) {
    return moment(rawValue).format('DD MMM YYYY');
  }

  if (dataType === 3) {
    return formatIncompletePhoneNumber(String(rawValue));
  }

  if (Array.isArray(rawValue)) {
    return rawValue.map((item: any) => item?.name ?? item).join(', ');
  }

  if (typeof rawValue === 'object' && rawValue !== null && 'name' in rawValue) {
    return rawValue.name ?? '';
  }

  const optionSet = BOOLEAN_FIELD_OPTIONS[code];
  if (optionSet) {
    const options = optionSet[lang] ?? optionSet.en;
    return options.find(o => String(o.id) === String(rawValue))?.name ?? String(rawValue);
  }

  if (typeof rawValue === 'boolean') {
    const options = YES_NO_OPTIONS[lang] ?? YES_NO_OPTIONS.en;
    return options.find(o => o.id === rawValue)?.name ?? String(rawValue);
  }

  return String(rawValue);
};
